import React, { useEffect, useState } from 'react';
import { documentNumberLabel, invoiceStatusLabel } from './documentLabels';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { SalesInvoice, SALES_INVOICE_STATUS_COLORS } from '../../interfaces/sales/salesInvoice.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { formatFc, isForeignCurrency } from '../../utils/fcDocuments';   // polish 4

const SalesInvoiceList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.SALES_INVOICE_MANAGE);
  const [rows, setRows] = useState<SalesInvoice[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [page, size]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setLoading(true);
      const res = await SalesInvoiceService.listByInstitution(institutionId, page, size);
      setRows(res.content);
      setTotal(res.totalElements);
    } catch (e) {
      toast.showApiError(e, 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.invoiceList.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Sales Invoices</Typography>
        {canManage && (
          <Button data-help="sales.invoiceList.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/sales/invoices/new')}>
            New Invoice
          </Button>
        )}
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} data-help-section="sales.invoiceList.table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-help="sales.invoiceList.col.number">Invoice #</TableCell>
                <TableCell data-help="sales.invoiceList.col.customer">Customer</TableCell>
                <TableCell data-help="sales.invoiceList.col.issueDate">Issue Date</TableCell>
                <TableCell data-help="sales.invoiceList.col.dueDate">Due Date</TableCell>
                <TableCell align="right" data-help="sales.invoiceList.col.total">Total</TableCell>
                <TableCell align="right" data-help="sales.invoiceList.col.paid">Paid</TableCell>
                <TableCell data-help="sales.invoiceList.col.status">Status</TableCell>
                <TableCell data-help="sales.invoiceList.col.overdue">Overdue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/sales/invoices/${row.id}`)}
                >
                  <TableCell>{documentNumberLabel(row.invoiceNumber)}</TableCell>
                  <TableCell>{row.customerName ?? '—'}</TableCell>
                  <TableCell>{formatDate(row.issueDate)}</TableCell>
                  <TableCell>{row.dueDate ? formatDate(row.dueDate) : '—'}</TableCell>
                  <TableCell align="right">{formatMoney(row.totalAmount)}{isForeignCurrency(row.currencyCode) && <Typography variant="caption" color="text.secondary" display="block">{formatFc(row.currencyCode!, row.foreignTotal)}</Typography>}</TableCell>
                  <TableCell align="right">{formatMoney(row.paidAmount)}</TableCell>
                  <TableCell>
                    <Chip
                      data-help="sales.invoiceList.status"
                      label={invoiceStatusLabel(row, humanizeEnum)}
                      color={SALES_INVOICE_STATUS_COLORS[row.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {row.overdue === true && (
                      <Chip data-help="sales.invoiceList.overdue" label="Overdue" color="error" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default SalesInvoiceList;
